import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Chrome, Eye, EyeOff, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function Signup() {
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setLoading(true);
    try {
      await signup(email, password, name);
      navigate("/");
    } catch (err) {
      setError(friendlyError(err.code));
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try { await loginWithGoogle(); navigate("/"); }
    catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="Stock Correlations" className="h-20 w-auto" />
        </div>

        <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-8">
          <h1 className="font-heading text-2xl font-black text-white mb-2">Create your account</h1>
          <p className="text-[#A1A1AA] text-sm mb-6">Free plan included · Upgrade to Premium anytime</p>

          {/* Plan comparison */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-[#09090B] border border-[#27272A] rounded-lg p-3">
              <p className="text-xs font-bold text-white mb-2">Free</p>
              {["Pattern matching", "90-day history", "5 metrics"].map(f => (
                <p key={f} className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                  <Check className="w-3 h-3 text-[#22c55e]" />{f}
                </p>
              ))}
            </div>
            <div className="bg-[#22c55e]/5 border border-[#22c55e]/30 rounded-lg p-3">
              <p className="text-xs font-bold text-[#22c55e] mb-2">Premium · $12.34/mo</p>
              {["Everything in Free", "β-Regression forecast", "Deep pattern match", "All weather metrics"].map(f => (
                <p key={f} className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                  <Check className="w-3 h-3 text-[#22c55e]" />{f}
                </p>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-[#F43F5E]/10 border border-[#F43F5E]/20 text-[#F43F5E] text-sm p-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1.5 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Daniel West" required
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm pl-10 pr-4 py-2.5 rounded-md focus:outline-none focus:border-[#38BDF8] placeholder-[#52525B]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm pl-10 pr-4 py-2.5 rounded-md focus:outline-none focus:border-[#38BDF8] placeholder-[#52525B]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input type={showPw ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" required
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm pl-10 pr-10 py-2.5 rounded-md focus:outline-none focus:border-[#38BDF8] placeholder-[#52525B]" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold h-11">
              {loading ? "Creating account..." : "Create Free Account"}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#27272A]" /></div>
            <div className="relative flex justify-center"><span className="bg-[#18181B] px-3 text-xs text-[#52525B]">or</span></div>
          </div>

          <Button onClick={handleGoogle} disabled={loading} variant="outline"
            className="w-full border-[#27272A] text-white hover:bg-[#27272A] h-11">
            <Chrome className="w-4 h-4 mr-2" /> Continue with Google
          </Button>

          <p className="text-center text-xs text-[#52525B] mt-4">
            By signing up you agree to our Terms of Service. Not financial advice.
          </p>
          <p className="text-center text-sm text-[#52525B] mt-3">
            Already have an account?{" "}
            <Link to="/login" className="text-[#38BDF8] hover:text-white">Sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function friendlyError(code) {
  const map = {
    "auth/email-already-in-use": "An account with that email already exists.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/weak-password": "Password must be at least 6 characters.",
  };
  return map[code] || "Signup failed. Please try again.";
}
